import { AgentConfig } from "../types";
import { freezeAgentConfig } from '../FreezeDiagnostic';

import systemPrompt from '@agents/hospitality/prompt.md?raw';
import { ProjectTools } from '../tools/ProjectTools';
import { KnowledgeTools } from '../tools/KnowledgeTools';
import { SocialTools } from '../tools/SocialTools';
import { UniversalTools } from '../tools/UniversalTools';
import { RoadTools } from '../tools/RoadTools';
import { buildDomainRetrievalTools, buildDomainRetrievalDeclarations } from '../tools/DomainTools';



const hospitalityRetrievalConfig = {
    'hospitality_bookings': { path: 'hospitality_bookings', requiresUserIdFilter: true },
    'riders': { path: 'riders', requiresUserIdFilter: true }
};
const hospitalityRetrievalTools = buildDomainRetrievalTools('Hospitality', hospitalityRetrievalConfig);
const hospitalityRetrievalDeclarations = buildDomainRetrievalDeclarations('Hospitality', hospitalityRetrievalConfig);

export const HospitalityAgent: AgentConfig = {
    id: 'hospitality',
    name: 'Hospitality Coordinator',
    description: 'Ensures exceptional artist care and venue hospitality through meticulous coordination.',
    color: 'bg-rose-500',
    category: 'department',
    systemPrompt: systemPrompt,
    get functions() {
        return {
            ...hospitalityRetrievalTools,
            search_places: RoadTools.search_places,
            get_place_details: RoadTools.get_place_details,
            get_distance_matrix: RoadTools.get_distance_matrix,
            create_project: ProjectTools.create_project,
            search_knowledge: KnowledgeTools.search_knowledge,
            generate_social_post: SocialTools.generate_social_post,
            web_extract: UniversalTools.web_extract,
            credential_vault: UniversalTools.credential_vault,
        } as Record<string, import('@/services/agent/types').AnyToolFunction>;
    },
    authorizedTools: ['list_domain_records', 'search_places', 'get_place_details', 'get_distance_matrix', 'create_project', 'search_knowledge', 'generate_social_post', 'web_extract', 'credential_vault'],
    tools: [{
        functionDeclarations: [
            ...hospitalityRetrievalDeclarations,
            {
                name: "search_places",
                description: "Find hotels, catering services, and vendors near venues through the secured Maps backend proxy. Requires sign-in; returns live Google Places results.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        query: { type: "STRING", description: "Search query (e.g., 'Hotels in Nashville')." },
                        type: { type: "STRING", description: "Optional place type." }
                    },
                    required: ["query"]
                }
            },
            {
                name: "get_place_details",
                description: "Unavailable until a place-details backend proxy is deployed. Calling this tool returns an explicit unavailable error and no place details.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        place_id: { type: "STRING", description: "Google Place ID." }
                    },
                    required: ["place_id"]
                }
            },
            {
                name: "get_distance_matrix",
                description: "Calculate real driving times between venues and accommodations through the secured Maps backend proxy. Requires sign-in; estimates exclude live traffic.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        origins: { type: "ARRAY", description: "Starting points.", items: { type: "STRING" } },
                        destinations: { type: "ARRAY", description: "Destinations.", items: { type: "STRING" } }
                    },
                    required: ["origins", "destinations"]
                }
            },
            {
                name: "create_project",
                description: "Create a hospitality coordination project.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        name: { type: "STRING", description: "Project name." },
                        type: { type: "STRING", enum: ["marketing", "creative", "music", "road"], description: "Project type." }
                    },
                    required: ["name"]
                }
            },
            {
                name: "search_knowledge",
                description: "Research touring standards and hospitality best practices.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        query: { type: "STRING", description: "Search query." }
                    },
                    required: ["query"]
                }
            },
            {
                name: "generate_social_post",
                description: "Draft hospitality updates for social media.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        platform: { type: "STRING", description: "Platform (e.g. Instagram)." },
                        topic: { type: "STRING", description: "Update content." }
                    },
                    required: ["topic"]
                }
            },
            {
                name: "web_extract",
                description: "Read bounded text from a public web page. This tool cannot log in, click, type, or submit forms.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        url: { type: "STRING", description: "Public HTTP(S) URL to read." }
                    },
                    required: ["url"]
                }
            },
            {
                name: "credential_vault",
                description: "Retrieve booking credentials for hotels and vendors securely.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        action: { type: "STRING", description: "retrieve" },
                        service: { type: "STRING", description: "Service name (e.g. Airbnb)" }
                    },
                    required: ["action", "service"]
                }
            }
        ]
    }]
};

export default freezeAgentConfig(HospitalityAgent);
